import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { galleryItems, GalleryItem } from '../../mocks/galleryItems';
import SEO, { SITE_URL } from '../../components/feature/SEO';

type FilterCategory = 'ALL' | 'BULLION' | 'EMBROIDERY' | 'CUSTOM';

const FILTERS: FilterCategory[] = ['ALL', 'BULLION', 'EMBROIDERY', 'CUSTOM'];

const galleryJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ImageGallery',
  name: '하이반텍스 갤러리 | HivanTex Portfolio',
  url: `${SITE_URL}/gallery`,
  description: '하이반텍스 장인의 손끝에서 탄생한 불리온 엠블럼 및 핸드메이드 자수 포트폴리오 갤러리',
  inLanguage: 'ko',
  author: {
    '@type': 'Organization',
    name: '하이반텍스 HivanTex',
    url: SITE_URL,
  },
  genre: ['Bullion Emblem', 'Handmade Embroidery', 'Custom'],
};

export default function GalleryPage() {
  const [active, setActive] = useState<FilterCategory>('ALL');

  const filtered: GalleryItem[] =
    active === 'ALL'
      ? galleryItems
      : galleryItems.filter((g) => g.category === active);

  return (
    <div className="min-h-screen font-sans bg-cream">
      <SEO jsonLd={galleryJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#181818] pt-40 pb-28 px-16 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50% 60% at 50% 60%, rgba(201,169,97,0.06) 0%, transparent 65%)',
          }}
        />
        <div className="absolute top-28 left-14 w-12 h-12 border-l border-t border-gold/25" />
        <div className="absolute top-28 right-14 w-12 h-12 border-r border-t border-gold/25" />
        <div className="max-w-[1300px] mx-auto relative z-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans">Portfolio</span>
            <div className="w-8 h-px bg-gold/50" />
          </div>
          <h1 className="font-serif text-cream text-6xl font-light leading-tight tracking-wide mb-6">
            Gallery
          </h1>
          <p className="text-cream/40 text-sm font-sans font-light">
            장인의 손끝에서 탄생한 작품들을감상하세요
          </p>
        </div>
      </section>

      {/* Filter tabs */}
      <section className="bg-cream py-12 px-16 border-b border-charcoal/10">
        <div className="max-w-[1300px] mx-auto flex items-center justify-center gap-0">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              className={`px-8 py-2 text-[10px] tracking-[0.22em] uppercase font-sans transition-all duration-200 cursor-pointer whitespace-nowrap ${
                active === f
                  ? 'bg-charcoal text-cream'
                  : 'text-charcoal/50 hover:text-gold hover:bg-charcoal/5'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      {/* Gallery grid */}
      <section className="py-16 px-16 bg-cream-light">
        <div className="max-w-[1300px] mx-auto">
          <div className="grid grid-cols-4 gap-3">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="group relative aspect-square overflow-hidden cursor-pointer"
                style={{ backgroundColor: item.bgColor }}
              >
                {/* Corner accents */}
                <div
                  className="absolute top-4 left-4 w-5 h-5 border-l border-t opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ borderColor: item.accentColor }}
                />
                <div
                  className="absolute bottom-4 right-4 w-5 h-5 border-r border-b opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ borderColor: item.accentColor }}
                />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                  <div className="w-8 h-px mb-5 opacity-30" style={{ backgroundColor: item.accentColor }} />
                  <p className="font-serif text-base font-light tracking-widest text-center mb-1"
                    style={{ color: item.accentColor }}>
                    {item.title}
                  </p>
                  <p className="text-[9px] tracking-[0.15em] uppercase font-sans font-light text-center"
                    style={{ color: `${item.accentColor}60` }}>
                    {item.subtitle}
                  </p>
                  <div className="w-8 h-px mt-5 opacity-30" style={{ backgroundColor: item.accentColor }} />
                </div>

                {/* Category + year */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-[9px] tracking-[0.15em] uppercase font-sans px-2 py-0.5"
                    style={{ border: `1px solid ${item.accentColor}30`, color: `${item.accentColor}80` }}>
                    {item.category}
                  </span>
                  <span className="text-[9px] font-sans" style={{ color: `${item.accentColor}50` }}>
                    {item.year}
                  </span>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ backgroundColor: `${item.accentColor}06` }} />
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-24 text-charcoal/30 font-sans text-sm tracking-wide">
              해당 카테고리의 작품이 없습니다.
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-cream py-20 px-16 text-center">
        <p className="text-charcoal/40 text-xs font-sans font-light leading-8 mb-8">
          귀사만을 위한 특별한 작품을 함께 만들어 보시겠습니까?
        </p>
        <a
          href="/contact"
          className="inline-flex items-center gap-3 border border-charcoal/40 text-charcoal text-[10px] tracking-[0.22em] uppercase px-10 py-3.5 hover:border-gold hover:text-gold transition-all duration-300 font-sans whitespace-nowrap cursor-pointer"
        >
          맞춤 제작 문의
          <i className="ri-arrow-right-line text-xs" />
        </a>
      </section>

      <Footer />
    </div>
  );
}
